import { Callout, DefaultEffects, DirectionalHint, getTheme, Stack, Text } from '@fluentui/react';
import React, { useState } from 'react';
import IvicosStrings from 'kits/language/stringKit';
import CustomAwayMessageModal from './CustomAwayMessageModal';

export interface IAwayOptionsMenuProps {
    isOpen: boolean;
    targetRef: React.RefObject<HTMLElement>;
    onOptionSelect: (message: string) => void;
    onClose: () => void;
}

const AWAY_OPTIONS = [
    { key: 'coffee', label: () => IvicosStrings.awayOptionCoffee },
    { key: 'coming_back', label: () => IvicosStrings.awayOptionComingBack },
    { key: 'custom', label: () => IvicosStrings.awayCustomMessage }
];

/**
 * Menu displayed when Away icon is clicked.
 * Shows predefined options and opens modal for custom message.
 */
const AwayOptionsMenu: React.FC<IAwayOptionsMenuProps> = ({ isOpen, targetRef, onOptionSelect, onClose }) => {
    const theme = getTheme();
    const [showCustomMessageModal, setShowCustomMessageModal] = useState(false);

    const handleOptionClick = (option: string) => {
        onOptionSelect(option);
        onClose();
    };

    const handleCreatePersonalMessage = () => {
        setShowCustomMessageModal(true);
    };

    const handleCustomMessageConfirm = (message: string) => {
        onOptionSelect(message);
        setShowCustomMessageModal(false);
        onClose();
    };

    const handleModalDismiss = () => {
        setShowCustomMessageModal(false);
    };

    if (!isOpen || !targetRef.current) return null;

    return (
        <Callout
            target={targetRef.current}
            onDismiss={onClose}
            directionalHint={DirectionalHint.bottomCenter}
            isBeakVisible={false}
            style={{
                padding: '12px',
                borderRadius: 8,
                boxShadow: DefaultEffects.elevation16,
                background: theme.palette.white,
                minWidth: 240
            }}
        >
            <Stack tokens={{ childrenGap: 8 }}>
                {/* All three predefined away options */}
                {AWAY_OPTIONS.slice(0, 3).map((option) => (
                    <Stack
                        key={option.key}
                        onClick={() => handleOptionClick(option.label())}
                        style={{
                            padding: '8px 12px',
                            borderRadius: 4,
                            cursor: 'pointer',
                            backgroundColor: theme.palette.neutralLighter,
                            transition: 'background-color 0.15s ease'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = theme.palette.neutralLight;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = theme.palette.neutralLighter;
                        }}
                    >
                        <Text style={{ fontSize: 14, color: theme.palette.black }}>{option.label()}</Text>
                    </Stack>
                ))}

                {/* Divider */}
                <div style={{ height: 1, background: theme.palette.neutralLight, margin: '4px 0' }} />

                {/* Create personal message row */}
                <Stack
                    onClick={handleCreatePersonalMessage}
                    style={{
                        padding: '8px 12px',
                        borderRadius: 4,
                        cursor: 'pointer',
                        backgroundColor: theme.palette.neutralLighter,
                        transition: 'background-color 0.15s ease',
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                        gap: 8
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = theme.palette.neutralLight;
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = theme.palette.neutralLighter;
                    }}
                >
                    <Text style={{ fontSize: 14, color: theme.palette.black }}>✏️ {IvicosStrings.awayCreatePersonalMessage}</Text>
                </Stack>
            </Stack>

            {/* Custom Away Message Modal */}
            <CustomAwayMessageModal isOpen={showCustomMessageModal} onConfirm={handleCustomMessageConfirm} onDismiss={handleModalDismiss} />
        </Callout>
    );
};

export default AwayOptionsMenu;
